import { Col, Row, TipBox } from '@orobix/orobix-fe-components'
import { Formik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import OpenTrainingButton from './OpenTrainingButton/OpenTrainingButton'
import SearchButton from './SearchButton/SearchButton'
import { TrainingUuid } from './SearchButton/utils'
import useStyles from './SearchForm.styles'
import SearchUuid from './SearchUuid/SearchUuid'

type SearchInfoMixin = {
  uuid?: string
}

export type SearchFormValues = SearchInfoMixin

interface SearchFormProps<T extends SearchFormValues> {
  initialValues: T
  validate: (values: T) => void
  onSubmit: (values: T, setSubmitting: (isSubmitting: boolean) => void) => void
}

const SearchForm = <T extends SearchFormValues>({
  initialValues,
  validate,
  onSubmit,
}: SearchFormProps<T>) => {
  const classes = useStyles()

  const { t } = useTranslation()

  const [trainingUuid, setTrainingUuid] = useState<TrainingUuid | undefined>()
  const [trainingSearched, setTrainingSearched] = useState(false)

  return (
    <Formik<T>
      validateOnMount
      initialValues={initialValues}
      validate={(values) => validate(values)}
      onSubmit={(values, { setSubmitting }) => onSubmit(values, setSubmitting)}
    >
      {({ values, isSubmitting, isValid }) => (
        <div role="form" className={classes.form}>
          <Row noBottomPadding className={classes.row}>
            <Col xs={3} xl={3}>
              <div>
                <SearchUuid
                  fieldName="uuid"
                  setTrainingSearched={setTrainingSearched}
                />

                <SearchButton
                  isDisabled={isSubmitting || !isValid}
                  isLoading={isSubmitting}
                  setTrainingUuid={setTrainingUuid}
                  setTrainingSearched={setTrainingSearched}
                  executionUuid={values.uuid}
                />

                {trainingUuid && (
                  <OpenTrainingButton
                    isDisabled={isSubmitting || !isValid || !trainingUuid}
                    isLoading={isSubmitting}
                    trainingUuid={trainingUuid}
                  />
                )}

                {!trainingUuid && trainingSearched && (
                  <p className={classes.errorColor}>
                    {t('No training found for uuid: {{ uuid }}', {
                      uuid: `${values.uuid}`,
                    })}
                  </p>
                )}
              </div>

              <div className={classes.tipBox}>
                <TipBox>
                  {t('To search training by uuid model:')}
                  <ul
                    className={classes.paddingLeft} // fix user agent stylesheet
                  >
                    <li>{t('insert uuid model')}</li>
                    <li>{t('press Search button')}</li>
                    <li>{t('press View training button')}</li>
                  </ul>
                </TipBox>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Formik>
  )
}

export default SearchForm
